/** What Raldo does with an element: it writes a slot, it leaves a layout part and a free element alone. */
export type ElementKind = 'slot' | 'part' | 'free'

/** How an element is edited: its words, its picture, or its look only. */
export type ElementType = 'text' | 'image' | 'object'

/** The identity of one selectable element of a page. */
export interface ElementInfo {
  eid: string
  kind: ElementKind
  type: ElementType
  /** The `data-slot` value of a slot, else `null`. */
  slotName: string | null
  /** The `data-part` value of a layout part, else `null`. */
  partName: string | null
  /** The name the editor gave a free element, else `null`. */
  freeName: string | null
}

const INLINE_TAGS = new Set([
  'EM',
  'STRONG',
  'B',
  'I',
  'U',
  'SPAN',
  'BR',
  'SMALL',
  'MARK',
  'SUP',
  'SUB',
])
const IMAGE_SLOT_TYPES = new Set(['image', 'logo'])
const ELEMENT_TYPES = new Set<string>(['text', 'image', 'object'])

/** The element a click on `target` selects: its closest ancestor with an eid, unless that is a page or the design root. */
export function selectableOf(target: Element | null): HTMLElement | null {
  const node = target?.closest<HTMLElement>('[data-eid]') ?? null
  return node && !isFrameRoot(node) ? node : null
}

/** The first selectable element among the elements under a point, topmost first. */
export function selectableAmong(stack: readonly Element[]): HTMLElement | null {
  for (const candidate of stack) {
    const found = selectableOf(candidate)
    if (found) return found
  }
  return null
}

/** True for a page element or the design root, which are never selected. */
export function isFrameRoot(element: Element): boolean {
  return element.classList.contains('slide') || element.hasAttribute('data-contract')
}

function hasOnlyInlineChildren(element: Element): boolean {
  return Array.from(element.children).every(
    (child) => INLINE_TAGS.has(child.tagName) && hasOnlyInlineChildren(child),
  )
}

/** The editing type of an element. */
export function elementTypeOf(element: HTMLElement): ElementType {
  const declared = element.dataset.type
  if (declared && ELEMENT_TYPES.has(declared)) return declared as ElementType
  const slotType = element.dataset.slotType
  if (element.tagName === 'IMG' || (slotType && IMAGE_SLOT_TYPES.has(slotType))) return 'image'
  if (slotType === 'text') return 'text'
  const hasText = (element.textContent ?? '').trim() !== ''
  if (hasText && hasOnlyInlineChildren(element)) return 'text'
  return 'object'
}

/** The kind of an element: slot, free, else part. */
export function elementKindOf(element: HTMLElement): ElementKind {
  if (element.hasAttribute('data-slot')) return 'slot'
  if (element.hasAttribute('data-free')) return 'free'
  return 'part'
}

/** The identity of a selectable element. */
export function describeElement(element: HTMLElement): ElementInfo {
  return {
    eid: element.dataset.eid ?? '',
    kind: elementKindOf(element),
    type: elementTypeOf(element),
    slotName: element.dataset.slot ?? null,
    partName: element.dataset.part ?? null,
    freeName: element.dataset.name ?? null,
  }
}

/** The part of a slot name before its index, so `images.0` is `images`. */
export function slotBaseName(slot: string): string {
  return slot.split('.')[0] ?? slot
}
